import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  TextField,
} from '@mui/material'
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff'
import { Context } from '../../../context/AuthContext'
import PageHeader from '../../Shared/PageHeader'
import DataTable from '../../Shared/DataTable'
import SelectSubCompany from '../../Shared/SelectSubCompany'
import ExportMenu from '../../Shared/ExportMenu'
import { useFeedback } from '../../Shared/FeedbackSnackbar'
import { getProductAssociationSearchService } from '../../../services/productService'
import { mensagemDeErro } from '../../../services/apiClient'
import { getRfidSituation, RFID_SITUATION } from '../../../const/productionStatus'
import { formatDateTime } from '../../../utils/format'
import useDebounce from '../../../utils/useDebounce'

const EMPTY = '—'

/**
 * Os filtros que sobraram são os que `GET /rfid-items` sabe responder.
 *
 * As colunas Ordem e Operador saíram: o contrato devolve `orderId` (UUID, sem
 * número legível) e não tem campo de usuário — quem leu a etiqueta é evento da
 * linha do tempo. Filtro que nunca casa é pior que filtro ausente.
 */
const INITIAL_FILTERS = {
  epc: '',
  product: '',
  situation: '',
  startDate: '',
  finishDate: '',
  location: '',
}

const includesText = (value, term) =>
  String(value || '')
    .toLowerCase()
    .includes(term.trim().toLowerCase())

const RfidQuery = () => {
  const { subCompany } = useContext(Context)
  const navigate = useNavigate()
  const { notify, FeedbackSnackbar } = useFeedback()

  const [subCompanyId, setSubCompanyId] = useState(
    subCompany.length === 1 ? subCompany[0].subCompanyId : ''
  )
  const [filters, setFilters] = useState(INITIAL_FILTERS)
  const [searchEpc, setSearchEpc] = useState('')
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [loading, setLoading] = useState(false)

  const setFilter = (field, value) => {
    setFilters(current => ({ ...current, [field]: value }))
    setPage(0)
  }

  const fetchRows = useCallback(async () => {
    if (!subCompanyId) {
      setRows([])
      setTotal(0)
      return
    }
    setLoading(true)
    try {
      const response = await getProductAssociationSearchService(
        rowsPerPage,
        page * rowsPerPage,
        subCompanyId,
        searchEpc,
        filters.situation
      )
      setRows(response.data.result || [])
      setTotal(response.data.total || 0)
    } catch (error) {
      setRows([])
      setTotal(0)
      notify(
        mensagemDeErro(error, 'Não foi possível carregar os RFIDs da filial selecionada.'),
        'error'
      )
    } finally {
      setLoading(false)
    }
  }, [subCompanyId, rowsPerPage, page, searchEpc, filters.situation, notify])

  useDebounce(() => setSearchEpc(filters.epc), 600, [filters.epc])

  useEffect(() => {
    fetchRows()
  }, [fetchRows])

  // Filial, EPC (`search`) e situação vão para a API; produto, local e período
  // são refinados no cliente. Predicado isolado para valer também no export.
  const passaFiltroCliente = useCallback(
    row => {
      if (filters.product && !includesText(row.productName, filters.product)) return false
      if (filters.location && !includesText(row.currentLocation, filters.location)) return false
      if (filters.startDate && new Date(row.createdAt) < new Date(filters.startDate)) return false
      if (filters.finishDate) {
        const finish = new Date(filters.finishDate)
        finish.setHours(23, 59, 59, 999)
        if (new Date(row.createdAt) > finish) return false
      }
      return true
    },
    [filters]
  )

  const filteredRows = useMemo(() => rows.filter(passaFiltroCliente), [rows, passaFiltroCliente])

  // Export leva todo o filtro: pagina a API em lotes (filial, EPC, situação) e
  // aplica o mesmo recorte de produto/local/período do cliente.
  const fetchAllForExport = useCallback(async () => {
    if (!subCompanyId) return []
    const BLOCO = 200
    const todas = []
    let offset = 0
    for (;;) {
      const response = await getProductAssociationSearchService(
        BLOCO,
        offset,
        subCompanyId,
        searchEpc,
        filters.situation
      )
      const itens = response.data.result || []
      todas.push(...itens)
      offset += BLOCO
      if (offset >= (response.data.total || 0) || itens.length === 0) break
    }
    return todas.filter(passaFiltroCliente)
  }, [subCompanyId, searchEpc, filters.situation, passaFiltroCliente])

  const columns = [
    {
      id: 'epc',
      label: 'EPC',
      element: row => {
        if (!row.epc) return EMPTY
        return (
          <Link
            component="button"
            type="button"
            underline="hover"
            onClick={() => navigate(`/RfidTimeline/${encodeURIComponent(row.epc)}`)}
          >
            {row.epc}
          </Link>
        )
      },
    },
    { id: 'product', label: 'Produto', element: row => row.productName || EMPTY },
    { id: 'lot', label: 'Lote', element: row => row.lot || EMPTY },
    {
      id: 'situation',
      label: 'Situação',
      element: row => {
        const situation = getRfidSituation(row.situation)
        return (
          <Chip
            size="small"
            label={row.situationLabel || situation.label}
            color={situation.color}
          />
        )
      },
    },
    { id: 'location', label: 'Localização', element: row => row.currentLocation || EMPTY },
    { id: 'created', label: 'Criado', element: row => formatDateTime(row.createdAt) },
    {
      id: 'lastRead',
      label: 'Última leitura',
      element: row => (row.lastReadAt ? formatDateTime(row.lastReadAt) : EMPTY),
    },
  ]

  const exportColumns = [
    { header: 'EPC', accessor: row => row.epc || EMPTY },
    { header: 'Produto', accessor: row => row.productName || EMPTY },
    { header: 'Lote', accessor: row => row.lot || EMPTY },
    {
      header: 'Situação',
      accessor: row => row.situationLabel || getRfidSituation(row.situation).label,
    },
    { header: 'Localização', accessor: row => row.currentLocation || EMPTY },
    { header: 'Criado', accessor: row => formatDateTime(row.createdAt) },
    {
      header: 'Última leitura',
      accessor: row => (row.lastReadAt ? formatDateTime(row.lastReadAt) : EMPTY),
    },
  ]

  return (
    <Box>
      <PageHeader
        title="Consulta de RFIDs"
        actions={
          <ExportMenu
            rows={filteredRows}
            resolveRows={fetchAllForExport}
            columns={exportColumns}
            fileName="consulta-rfids"
            title="Consulta de RFIDs"
            disabled={total === 0}
          />
        }
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <SelectSubCompany
              value={subCompanyId}
              onChange={event => setSubCompanyId(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="EPC"
              value={filters.epc}
              onChange={event => setFilter('epc', event.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Produto"
              value={filters.product}
              onChange={event => setFilter('product', event.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel id="rfid-situation-label">Situação</InputLabel>
              <Select
                labelId="rfid-situation-label"
                label="Situação"
                value={filters.situation}
                onChange={event => setFilter('situation', event.target.value)}
              >
                <MenuItem value="">Todas</MenuItem>
                {Object.entries(RFID_SITUATION).map(([value, { label }]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="De"
              type="date"
              value={filters.startDate}
              onChange={event => setFilter('startDate', event.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Até"
              type="date"
              value={filters.finishDate}
              onChange={event => setFilter('finishDate', event.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Localização"
              value={filters.location}
              onChange={event => setFilter('location', event.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<FilterAltOffIcon />}
              onClick={() => {
                setFilters(INITIAL_FILTERS)
                setPage(0)
              }}
            >
              Limpar filtros
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <DataTable
        columns={columns}
        rows={filteredRows}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        total={total}
        onPageChange={(event, newPage) => setPage(newPage)}
        onRowsPerPageChange={event => {
          setRowsPerPage(Number(event.target.value))
          setPage(0)
        }}
        rowKey={row => row.id || row.epc}
        emptyMessage={
          subCompanyId
            ? 'Nenhum RFID encontrado para os filtros informados.'
            : 'Selecione uma filial para consultar os RFIDs.'
        }
      />

      <FeedbackSnackbar />
    </Box>
  )
}

export default RfidQuery
